import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useBodyScrollLock } from './useDialogBehavior.js';

// Modali aperti, dal più vecchio al più recente: Esc chiude solo quello in cima.
const pila: symbol[] = [];

/**
 * Renderizza un modale su `document.body` e ne uniforma il comportamento:
 * - un overlay `position: fixed` annidato in un antenato con `transform`/animazione si centra su
 *   quell'antenato invece che sulla viewport: il portal lo centra sempre nella finestra;
 * - blocca lo scroll della pagina finché è montato;
 * - se `onClose` è passato, Esc chiude il modale (solo quello più in alto, se ce n'è più d'uno).
 *
 * Va montato solo quando il modale è aperto (`{aperto && <ModalPortal>…</ModalPortal>}`).
 */
export function ModalPortal({ children, onClose }: { children: ReactNode; onClose?: () => void }) {
  useBodyScrollLock();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const id = Symbol('modal');
    pila.push(id);
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && pila[pila.length - 1] === id) onCloseRef.current?.();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      pila.splice(pila.indexOf(id), 1);
    };
  }, []);

  return createPortal(children, document.body);
}
